import { ElementState, ElementStateMap, MetaType, TypeArray } from "../types"
import {
  actionCreatorGuard,
  actionCreatorTypeGuard,
  reducerGuard,
  processActionMeta,
  getObjectMapTypeReducerGuard
} from "../reducers"
import { ElementStateSchema } from "../schemas"

import _ from "lodash"

const kind = "element_course_state"

export type ElementCourseState = {
  kind: typeof kind
  version: number
  courseId: string
  schoolYear: number
  elements: ElementStateMap
  meta: MetaType
}

export const reducerAction = {
  meta: actionCreatorGuard<ElementCourseState, MetaType>("meta", kind),
  courseId: actionCreatorGuard<ElementCourseState, string>("courseId", kind),
  schoolYear: actionCreatorGuard<ElementCourseState, number>(
    "schoolYear",
    kind
  ),
  elements: actionCreatorGuard<ElementCourseState, ElementStateMap>(
    "elements",
    kind
  ),
  __reset: actionCreatorTypeGuard<
    TypeArray,
    Partial<ElementCourseState> | null
  >("reset", "__reset/ElementState")
}

// Type safety to ensure we have a defined action for each property
type ReducerActions = {
  [key in
    | keyof Omit<ElementCourseState, "kind" | "version">
    | "__reset"]: (typeof reducerAction)[key][0]
}

// Our exported actions
export const elementCourseStateAction = Object.fromEntries(
  Object.entries(reducerAction).map(k => [k[0], k[1][0]])
) as ReducerActions

const validationOptions = {
  strict: true,
  abortEarly: false,
  stripUnknown: true,
  recursive: true
}

export const elementCourseStateReducer = reducerGuard<ElementCourseState>(kind)
  .case(...reducerAction.meta)
  .case(...reducerAction.courseId)
  .case(...reducerAction.schoolYear)
  .case(
    elementCourseStateAction.__reset,
    (_state, newState) => ({ ...newState } as ElementCourseState)
  )
  .default((state, action: any) => {
    action = processActionMeta(action)
    let prevState = _.cloneDeep(state)

    const {
      prop,
      idx,
      nodes
    }: { prop: keyof ElementCourseState; idx: number; nodes: Array<any> } =
      action?.meta

    if (prop === "elements") {
      if (prevState?.[prop]?.[idx] === undefined) {
        prevState[prop][idx] = ElementStateSchema.cast({}, validationOptions)
      }
    }

    switch (action?.meta?.prop) {
      case "elements":
        return getObjectMapTypeReducerGuard<
          ElementCourseState,
          ElementStateMap,
          ElementState
        >("elements", "elements", "ElementState", prevState, action)
    }

    return prevState
  })
